/**
 * Procedural First-Person Weapon Controller for Aim Assist Training Lab
 * Features:
 * - 4 High-contrast dual-tone weapon archetypes (Rifle, SMG, Sniper, Pistol)
 * - True slide blowback & physics recoil kinematics
 * - Muzzle flash starburst & barrel light
 * - 3D brass casing ejection
 * - Mechanical reload state machine with ammo magazines
 */

import * as THREE from 'three';
import { WEAPON_TYPES } from './constants.js';

export class WeaponController {
  constructor(weaponGroup, sceneManager, audioManager, settingsStore) {
    this.group = weaponGroup;
    this.sceneManager = sceneManager;
    this.audioManager = audioManager;
    this.settingsStore = settingsStore;

    // Active weapon archetype
    this.currentWeaponKey = settingsStore.get('selectedWeapon') || 'rifle';
    this.weaponConfig = WEAPON_TYPES[this.currentWeaponKey] || WEAPON_TYPES.rifle;

    // Root Group & Meshes
    this.weaponRoot = new THREE.Group();
    this.barrelTip = new THREE.Object3D();
    this.slideMesh = null;
    this.muzzleLight = null;
    this.muzzleFlashMesh = null;
    this.muzzleTimer = 0;
    this.slideKick = 0;

    // Ejected Brass Casings Pool
    this.casings = [];
    this.casingsPool = [];
    this.initCasingsPool();

    // Ammo & State
    this.maxAmmo = this.weaponConfig.magSize;
    this.ammo = this.maxAmmo;
    this.isReloading = false;
    this.reloadProgress = 0;
    this.reloadDuration = this.weaponConfig.reloadTimeSec;
    this.fireInterval = this.weaponConfig.fireRateMs;
    this.lastFireTime = 0;

    // ADS (Aim-Down-Sights) State & FOV Zoom
    this.isAds = false;
    this.adsProgress = 0;
    this.baseFov = Number(this.settingsStore.get('fov')) || 103;
    this.currentFov = this.baseFov;
    this.hipPos = new THREE.Vector3(0.24, -0.22, -0.46);
    this.adsPos = new THREE.Vector3(0.0, -0.085, -0.28);
    this.currentPos = this.hipPos.clone();

    // Recoil Kinematics
    this.recoilOffsetZ = 0;
    this.recoilPitch = 0;
    this.recoilYaw = 0;
    this.recoilBloom = 0;
    this.weaponShakeTimer = 0;
    this.onAdsChange = null;

    this.buildCurrentWeapon();
    this.group.add(this.weaponRoot);

    settingsStore.subscribe((s) => {
      if (s.selectedWeapon && s.selectedWeapon !== this.currentWeaponKey) {
        this.switchWeapon(s.selectedWeapon);
      }
      if (s.fov) {
        this.baseFov = Number(s.fov);
        if (!this.isAds && this.sceneManager) {
          this.sceneManager.updateFov(this.baseFov);
        }
      }
    });
  }

  initCasingsPool() {
    const casingGeo = new THREE.CylinderGeometry(0.007, 0.007, 0.024, 6);
    const casingMat = new THREE.MeshStandardMaterial({
      color: 0xf59e0b, // Golden brass
      metalness: 0.95,
      roughness: 0.2
    });

    for (let i = 0; i < 20; i++) {
      const mesh = new THREE.Mesh(casingGeo, casingMat);
      mesh.visible = false;
      this.sceneManager.scene.add(mesh);
      this.casingsPool.push({
        mesh,
        velocity: new THREE.Vector3(),
        rotVel: new THREE.Vector3(),
        life: 0,
        maxLife: 0.6
      });
    }
  }

  buildCurrentWeapon() {
    // Clear existing weapon geometry
    while (this.weaponRoot.children.length > 0) {
      const obj = this.weaponRoot.children[0];
      this.weaponRoot.remove(obj);
      if (obj.geometry) obj.geometry.dispose();
      if (obj.material) obj.material.dispose();
    }

    this.slideMesh = null;
    this.barrelTip.position.set(0, 0.02, -0.44);

    if (this.currentWeaponKey === 'smg') {
      this.buildSmg();
    } else if (this.currentWeaponKey === 'sniper') {
      this.buildSniper();
    } else if (this.currentWeaponKey === 'pistol') {
      this.buildPistol();
    } else {
      this.buildRifle();
    }

    // Attach Muzzle Flash Light & Starburst
    this.setupMuzzleFX();
    this.weaponRoot.add(this.barrelTip);
    this.weaponRoot.position.copy(this.hipPos);
  }

  setupMuzzleFX() {
    // Dynamic Point Light at Muzzle Tip
    this.muzzleLight = new THREE.PointLight(0x38bdf8, 0, 4.0);
    this.muzzleLight.position.copy(this.barrelTip.position);
    this.weaponRoot.add(this.muzzleLight);

    // 3D Starburst geometry for visual gunshot flash
    const starGeo = new THREE.OctahedronGeometry(0.065, 0);
    const starMat = new THREE.MeshBasicMaterial({
      color: 0x67e8f9,
      transparent: true,
      opacity: 0.95
    });
    this.muzzleFlashMesh = new THREE.Mesh(starGeo, starMat);
    this.muzzleFlashMesh.position.copy(this.barrelTip.position);
    this.muzzleFlashMesh.visible = false;
    this.weaponRoot.add(this.muzzleFlashMesh);
  }

  /**
   * Weapon 1: Tactical Assault Rifle (Vandal / AK-47)
   * Low-profile receiver with ultra-wide open holographic reflex optic & crystal glass
   */
  buildRifle() {
    const metalMat = new THREE.MeshStandardMaterial({ color: 0x1e293b, metalness: 0.85, roughness: 0.25 });
    const silverMat = new THREE.MeshStandardMaterial({ color: 0xe2e8f0, metalness: 0.95, roughness: 0.15 });
    const carbonMat = new THREE.MeshStandardMaterial({ color: 0x0f172a, metalness: 0.5, roughness: 0.6 });

    // Streamlined low receiver & stock (completely below sightline)
    const receiver = new THREE.Mesh(new THREE.BoxGeometry(0.038, 0.032, 0.28), metalMat);
    receiver.position.set(0, -0.038, 0);
    this.weaponRoot.add(receiver);

    const stock = new THREE.Mesh(new THREE.BoxGeometry(0.032, 0.040, 0.18), carbonMat);
    stock.position.set(0, -0.042, 0.22);
    this.weaponRoot.add(stock);

    // Slim low barrel
    const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.009, 0.011, 0.26, 8), silverMat);
    barrel.rotation.x = Math.PI / 2;
    barrel.position.set(0, -0.020, -0.24);
    this.weaponRoot.add(barrel);

    // Muzzle brake
    const muzzle = new THREE.Mesh(new THREE.BoxGeometry(0.022, 0.022, 0.045), metalMat);
    muzzle.position.set(0, -0.020, -0.38);
    this.weaponRoot.add(muzzle);

    // Slide / Bolt Carrier
    this.slideMesh = new THREE.Mesh(new THREE.BoxGeometry(0.034, 0.018, 0.12), silverMat);
    this.slideMesh.position.set(0, -0.018, -0.03);
    this.weaponRoot.add(this.slideMesh);

    // HOLLOW Reflex Sight Bezel with Crystal Clear Transparent Glass
    // Optical center aligns precisely at y = 0.0 in camera view during ADS
    const opticGroup = new THREE.Group();
    opticGroup.position.set(0, 0.052, -0.06);

    const hoodMat = new THREE.MeshStandardMaterial({ color: 0x0f172a, metalness: 0.95, roughness: 0.2 });
    const glassMat = new THREE.MeshStandardMaterial({
      color: 0x38bdf8,
      transparent: true,
      opacity: 0.04,
      roughness: 0.02,
      metalness: 0.05,
      depthWrite: false
    });

    // Thin outer frame (posts: 0.0025, bars: 0.0025)
    const leftPost = new THREE.Mesh(new THREE.BoxGeometry(0.0025, 0.044, 0.015), hoodMat);
    leftPost.position.set(-0.026, 0, 0);
    opticGroup.add(leftPost);

    const rightPost = new THREE.Mesh(new THREE.BoxGeometry(0.0025, 0.044, 0.015), hoodMat);
    rightPost.position.set(0.026, 0, 0);
    opticGroup.add(rightPost);

    const topBar = new THREE.Mesh(new THREE.BoxGeometry(0.0545, 0.0025, 0.015), hoodMat);
    topBar.position.set(0, 0.022, 0);
    opticGroup.add(topBar);

    const bottomMount = new THREE.Mesh(new THREE.BoxGeometry(0.0545, 0.0035, 0.02), hoodMat);
    bottomMount.position.set(0, -0.022, 0);
    opticGroup.add(bottomMount);

    // 100% Transparent Glass Window Pane
    const glass = new THREE.Mesh(new THREE.PlaneGeometry(0.050, 0.042), glassMat);
    opticGroup.add(glass);

    // Holographic Red Reticle Ring + Center Dot
    const ringMat = new THREE.MeshBasicMaterial({ color: 0xff3b30, side: THREE.DoubleSide });
    const reticleRing = new THREE.Mesh(
      new THREE.RingGeometry(0.0045, 0.006, 24),
      ringMat
    );
    reticleRing.position.set(0, 0, 0.001);
    opticGroup.add(reticleRing);

    const dotMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
    const reticleDot = new THREE.Mesh(
      new THREE.SphereGeometry(0.0014, 8, 8),
      dotMat
    );
    reticleDot.position.set(0, 0, 0.002);
    opticGroup.add(reticleDot);

    this.weaponRoot.add(opticGroup);

    // Curved Ammo Magazine
    const mag = new THREE.Mesh(new THREE.BoxGeometry(0.032, 0.12, 0.055), carbonMat);
    mag.position.set(0, -0.10, -0.04);
    mag.rotation.x = -0.22;
    this.weaponRoot.add(mag);

    // Grip
    const grip = new THREE.Mesh(new THREE.BoxGeometry(0.034, 0.09, 0.045), carbonMat);
    grip.position.set(0, -0.085, 0.07);
    grip.rotation.x = 0.28;
    this.weaponRoot.add(grip);

    this.barrelTip.position.set(0, -0.020, -0.42);
    this.hipPos.set(0.22, -0.20, -0.42);
    this.adsPos.set(0.0, -0.052, -0.26);
  }

  /**
   * Weapon 2: Rapid SMG (Spectre / R-99)
   * Ultra-compact chassis with circular reflex optic & clear sightline
   */
  buildSmg() {
    const metalMat = new THREE.MeshStandardMaterial({ color: 0x1e293b, metalness: 0.9, roughness: 0.2 });
    const silverMat = new THREE.MeshStandardMaterial({ color: 0x94a3b8, metalness: 0.95, roughness: 0.15 });

    // Compact Low Profile Receiver
    const receiver = new THREE.Mesh(new THREE.BoxGeometry(0.034, 0.032, 0.22), metalMat);
    receiver.position.set(0, -0.035, 0);
    this.weaponRoot.add(receiver);

    // Short Low Barrel
    const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.009, 0.011, 0.16, 8), silverMat);
    barrel.rotation.x = Math.PI / 2;
    barrel.position.set(0, -0.018, -0.16);
    this.weaponRoot.add(barrel);

    // Forward Foregrip
    const foregrip = new THREE.Mesh(new THREE.BoxGeometry(0.024, 0.06, 0.028), metalMat);
    foregrip.position.set(0, -0.075, -0.12);
    this.weaponRoot.add(foregrip);

    // Slide
    this.slideMesh = new THREE.Mesh(new THREE.BoxGeometry(0.032, 0.018, 0.10), silverMat);
    this.slideMesh.position.set(0, -0.014, -0.02);
    this.weaponRoot.add(this.slideMesh);

    // Compact Hollow Circular Optic Hood with Green Reticle Dot
    const opticGroup = new THREE.Group();
    opticGroup.position.set(0, 0.046, -0.04);

    const torusHood = new THREE.Mesh(
      new THREE.TorusGeometry(0.022, 0.002, 8, 24),
      new THREE.MeshStandardMaterial({ color: 0x0f172a, metalness: 0.95, roughness: 0.2 })
    );
    opticGroup.add(torusHood);

    const glass = new THREE.Mesh(
      new THREE.CircleGeometry(0.020, 24),
      new THREE.MeshStandardMaterial({ color: 0x22c55e, transparent: true, opacity: 0.04, depthWrite: false })
    );
    opticGroup.add(glass);

    const dot = new THREE.Mesh(
      new THREE.SphereGeometry(0.0016, 8, 8),
      new THREE.MeshBasicMaterial({ color: 0x22c55e })
    );
    dot.position.set(0, 0, 0.002);
    opticGroup.add(dot);

    this.weaponRoot.add(opticGroup);

    // Straight Extended Magazine
    const mag = new THREE.Mesh(new THREE.BoxGeometry(0.028, 0.13, 0.04), metalMat);
    mag.position.set(0, -0.10, -0.02);
    this.weaponRoot.add(mag);

    // Grip
    const grip = new THREE.Mesh(new THREE.BoxGeometry(0.032, 0.08, 0.04), metalMat);
    grip.position.set(0, -0.08, 0.06);
    grip.rotation.x = 0.25;
    this.weaponRoot.add(grip);

    this.barrelTip.position.set(0, -0.018, -0.26);
    this.hipPos.set(0.18, -0.18, -0.36);
    this.adsPos.set(0.0, -0.046, -0.22);
  }

  /**
   * Weapon 3: Heavy Precision Sniper (Operator / AWP)
   * Beautiful in hip-fire; 100% hidden in ADS so player looks purely through the fullscreen scope overlay!
   */
  buildSniper() {
    const metalMat = new THREE.MeshStandardMaterial({ color: 0x1f2937, metalness: 0.8, roughness: 0.3 });
    const barrelMat = new THREE.MeshStandardMaterial({ color: 0x475569, metalness: 0.95, roughness: 0.15 });
    const goldNeon = new THREE.MeshBasicMaterial({ color: 0xfacc15 });

    // Long Body Chassis
    const chassis = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.055, 0.42), metalMat);
    chassis.position.set(0, -0.03, 0);
    this.weaponRoot.add(chassis);

    // Heavy Fluted Barrel
    const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.014, 0.017, 0.50, 10), barrelMat);
    barrel.rotation.x = Math.PI / 2;
    barrel.position.set(0, -0.01, -0.42);
    this.weaponRoot.add(barrel);

    // Massive Muzzle Brake
    const muzzle = new THREE.Mesh(new THREE.BoxGeometry(0.035, 0.03, 0.07), metalMat);
    muzzle.position.set(0, -0.01, -0.68);
    this.weaponRoot.add(muzzle);

    // Large Sniper Scope Tube
    const scopeTube = new THREE.Mesh(new THREE.CylinderGeometry(0.018, 0.022, 0.26, 12), metalMat);
    scopeTube.rotation.x = Math.PI / 2;
    scopeTube.position.set(0, 0.045, -0.06);
    this.weaponRoot.add(scopeTube);

    // Bolt Action Handle
    this.slideMesh = new THREE.Mesh(new THREE.CylinderGeometry(0.005, 0.005, 0.05, 6), barrelMat);
    this.slideMesh.rotation.z = Math.PI / 2;
    this.slideMesh.position.set(0.034, 0.01, 0.06);
    this.weaponRoot.add(this.slideMesh);

    // Stock
    const stock = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.065, 0.24), metalMat);
    stock.position.set(0, -0.03, 0.30);
    this.weaponRoot.add(stock);

    // Grip
    const grip = new THREE.Mesh(new THREE.BoxGeometry(0.036, 0.10, 0.05), metalMat);
    grip.position.set(0, -0.08, 0.09);
    grip.rotation.x = 0.22;
    this.weaponRoot.add(grip);

    // Neon accent rings on scope
    const ring = new THREE.Mesh(new THREE.TorusGeometry(0.023, 0.0025, 8, 16), goldNeon);
    ring.position.set(0, 0.045, -0.15);
    this.weaponRoot.add(ring);

    this.barrelTip.position.set(0, -0.01, -0.72);
    this.hipPos.set(0.24, -0.22, -0.46);
    this.adsPos.set(0.0, -0.045, -0.30);
  }

  /**
   * Weapon 4: Tactical High-Caliber Pistol (Sheriff / Deagle)
   * Open 3-dot notch sightline: rear notch is split so front orange post is 100% visible
   */
  buildPistol() {
    const metalMat = new THREE.MeshStandardMaterial({ color: 0x334155, metalness: 0.9, roughness: 0.2 });
    const chromeSlide = new THREE.MeshStandardMaterial({ color: 0xf1f5f9, metalness: 0.98, roughness: 0.1 });
    const orangeNeon = new THREE.MeshBasicMaterial({ color: 0xf97316 });

    // Lower Frame & Handle
    const frame = new THREE.Mesh(new THREE.BoxGeometry(0.034, 0.036, 0.18), metalMat);
    frame.position.set(0, -0.02, 0);
    this.weaponRoot.add(frame);

    const grip = new THREE.Mesh(new THREE.BoxGeometry(0.032, 0.09, 0.05), metalMat);
    grip.position.set(0, -0.075, 0.03);
    grip.rotation.x = 0.26;
    this.weaponRoot.add(grip);

    // Chrome Heavy Slide (Blowback animated)
    this.slideMesh = new THREE.Mesh(new THREE.BoxGeometry(0.036, 0.030, 0.19), chromeSlide);
    this.slideMesh.position.set(0, 0.012, -0.02);
    this.weaponRoot.add(this.slideMesh);

    // Split Rear Notch (two posts with an open central gap)
    const rearLeft = new THREE.Mesh(new THREE.BoxGeometry(0.004, 0.010, 0.006), metalMat);
    rearLeft.position.set(-0.010, 0.032, 0.06);
    this.weaponRoot.add(rearLeft);

    const rearRight = new THREE.Mesh(new THREE.BoxGeometry(0.004, 0.010, 0.006), metalMat);
    rearRight.position.set(0.010, 0.032, 0.06);
    this.weaponRoot.add(rearRight);

    // Front Orange Sight Post (perfectly visible through rear notch gap!)
    const frontSight = new THREE.Mesh(new THREE.BoxGeometry(0.0035, 0.009, 0.006), orangeNeon);
    frontSight.position.set(0, 0.032, -0.10);
    this.weaponRoot.add(frontSight);

    this.barrelTip.position.set(0, 0.012, -0.14);
    this.hipPos.set(0.16, -0.16, -0.32);
    this.adsPos.set(0.0, -0.032, -0.20);
  }

  getMuzzleWorldPosition() {
    const worldVec = new THREE.Vector3();
    this.barrelTip.getWorldPosition(worldVec);
    return worldVec;
  }

  setAds(isAiming) {
    if (this.isAds !== isAiming) {
      this.isAds = isAiming;
      if (this.onAdsChange) {
        this.onAdsChange(isAiming);
      }
      if (this.audioManager) {
        if (isAiming) {
          this.audioManager.playScopeIn(this.currentWeaponKey);
        } else {
          this.audioManager.playScopeOut(this.currentWeaponKey);
        }
      }
    }
  }

  switchWeapon(weaponKey) {
    if (!WEAPON_TYPES[weaponKey]) return;
    this.currentWeaponKey = weaponKey;
    this.weaponConfig = WEAPON_TYPES[weaponKey];

    this.maxAmmo = this.weaponConfig.magSize;
    this.ammo = this.maxAmmo;
    this.reloadDuration = this.weaponConfig.reloadTimeSec;
    this.fireInterval = this.weaponConfig.fireRateMs;
    this.isReloading = false;
    this.reloadProgress = 0;
    this.isAds = false;
    this.adsProgress = 0;

    // Reset FOV to base
    this.baseFov = Number(this.settingsStore.get('fov')) || 103;
    this.currentFov = this.baseFov;
    if (this.sceneManager) {
      this.sceneManager.updateFov(this.baseFov);
    }

    this.buildCurrentWeapon();
  }

  canFire(currentTime) {
    if (this.isReloading) return false;
    if (this.ammo <= 0) return false;
    return currentTime - this.lastFireTime >= this.fireInterval;
  }

  fire(currentTime, spreadPenalty = 0) {
    if (this.isReloading) return false;

    if (this.ammo <= 0) {
      this.audioManager.playEmptyClick();
      this.reload();
      return false;
    }

    if (currentTime - this.lastFireTime < this.fireInterval) {
      return false;
    }

    this.ammo--;
    this.lastFireTime = currentTime;

    // Recoil Impulse
    this.recoilOffsetZ = 0.045;
    this.recoilPitch = this.weaponConfig.recoilPitchPerShot;
    this.recoilYaw = (Math.random() - 0.5) * this.weaponConfig.recoilYawPerShot;
    this.recoilBloom = Math.min(this.recoilBloom + 0.35 + spreadPenalty, 1.8);
    this.slideKick = 0.04; // 4cm slide blowback

    // Muzzle Flash
    if (this.muzzleLight) {
      this.muzzleLight.intensity = 3.5;
      this.muzzleTimer = 0.045; // 45ms
    }
    if (this.muzzleFlashMesh) {
      this.muzzleFlashMesh.visible = true;
      this.muzzleFlashMesh.rotation.z = Math.random() * Math.PI;
    }

    // Eject Brass Casing
    this.ejectCasing();

    // Audio
    this.audioManager.playShot(this.currentWeaponKey);
    return true;
  }

  ejectCasing() {
    if (!this.settingsStore.get('casingEjection')) return;

    for (const c of this.casingsPool) {
      if (!c.mesh.visible) {
        const muzzlePos = this.getMuzzleWorldPosition();
        c.mesh.position.copy(muzzlePos);
        c.mesh.position.x += 0.04;
        c.mesh.visible = true;
        c.life = 0;
        c.velocity.set(
          1.8 + Math.random() * 0.8, // Pop right
          1.5 + Math.random() * 0.6, // Pop up
          (Math.random() - 0.5) * 0.8
        );
        c.rotVel.set(Math.random() * 15, Math.random() * 15, Math.random() * 15);
        this.casings.push(c);
        break;
      }
    }
  }

  reload() {
    if (this.isReloading || this.ammo === this.maxAmmo) return;
    this.isReloading = true;
    this.reloadProgress = 0;
    this.audioManager.playReload(this.reloadDuration);
  }

  applyWeaponShake(pitchKick = 0.02, yawKick = 0, isAds = false, swayOffset = null) {
    const adsFactor = isAds ? 0.65 : 1.0;
    this.recoilOffsetZ = Math.min(this.recoilOffsetZ + 0.065 * adsFactor, 0.12);
    this.recoilPitch = Math.min(this.recoilPitch + Math.max(pitchKick * 4.2, 0.08) * adsFactor, 0.32);
    this.recoilYaw += (Math.random() - 0.5) * 0.025 * adsFactor;
    if (swayOffset && typeof swayOffset.x === 'number') {
      // Visual barrel tilt telegraphing horizontal spray direction (crucial for Valorant rifle spray)
      this.barrelRoll = -swayOffset.x * 2.8 * adsFactor;
    }
    this.weaponShakeTimer = 0.09; // 90ms punchy mechanical shake
    this.slideKick = 0.055;
  }

  update(delta, currentTime) {
    // 1. Muzzle Flash Decay
    if (this.muzzleTimer > 0) {
      this.muzzleTimer -= delta;
      if (this.muzzleTimer <= 0) {
        if (this.muzzleLight) this.muzzleLight.intensity = 0;
        if (this.muzzleFlashMesh) this.muzzleFlashMesh.visible = false;
      }
    }

    // 2. Slide Blowback Return Lerp
    if (this.slideMesh) {
      this.slideKick = Math.max(0, this.slideKick - delta * 0.45);
      this.slideMesh.position.z = -0.04 + this.slideKick;
    }

    // 3. Smooth ADS Transition & Dynamic Optical FOV Zoom
    const targetAds = this.isAds ? 1.0 : 0.0;
    const adsSpeed = this.currentWeaponKey === 'sniper' ? 18 : 14;
    this.adsProgress += (targetAds - this.adsProgress) * Math.min(delta * adsSpeed, 1);
    this.currentPos.lerpVectors(this.hipPos, this.adsPos, this.adsProgress);

    // Dynamic Camera FOV Zoom scaling realistically by weapon
    this.baseFov = Number(this.settingsStore.get('fov')) || 103;
    const targetZoom = this.weaponConfig.adsZoom || 1.25;
    const targetFov = this.isAds ? (this.baseFov / targetZoom) : this.baseFov;
    this.currentFov += (targetFov - this.currentFov) * Math.min(delta * adsSpeed, 1);
    if (this.sceneManager && Math.abs(this.currentFov - this.sceneManager.camera.fov) > 0.05) {
      this.sceneManager.updateFov(this.currentFov);
    }

    // For heavy sniper, hide weapon body immediately when aiming so player looks directly through scope overlay
    if (this.currentWeaponKey === 'sniper') {
      this.weaponRoot.visible = !this.isAds && this.adsProgress < 0.15;
    } else {
      this.weaponRoot.visible = true;
    }

    // 4. Recoil Recovery
    const recSpeed = this.weaponConfig.recoilRecoverySpeed || 8.0;
    this.recoilOffsetZ = Math.max(0, this.recoilOffsetZ - delta * 0.55);
    this.recoilPitch = Math.max(0, this.recoilPitch - delta * recSpeed * 0.22);
    this.recoilYaw *= Math.max(0, 1 - delta * 12.0);
    this.recoilBloom = Math.max(0, this.recoilBloom - delta * 2.4);
    if (this.barrelRoll) {
      this.barrelRoll *= Math.max(0, 1 - delta * 8.0);
    }

    // Mechanical Weapon Shake impulse decay (sharp visible vibration)
    let shakeJitterX = 0;
    let shakeJitterY = 0;
    if (this.weaponShakeTimer > 0) {
      this.weaponShakeTimer -= delta;
      const intensity = this.weaponShakeTimer / 0.09;
      shakeJitterX = (Math.random() - 0.5) * 0.012 * intensity;
      shakeJitterY = (Math.random() - 0.5) * 0.012 * intensity;
    }

    // 5. Reload Mechanical Animation Handling
    let reloadOffsetY = 0;
    let reloadRotX = 0;
    if (this.isReloading) {
      this.reloadProgress += delta / this.reloadDuration;
      if (this.reloadProgress >= 1.0) {
        this.isReloading = false;
        this.ammo = this.maxAmmo;
        this.reloadProgress = 0;
      } else {
        // Natural curved arc: dips down, inserts mag, returns
        const dip = Math.sin(this.reloadProgress * Math.PI);
        reloadOffsetY = -0.14 * dip;
        reloadRotX = -0.42 * dip;
      }
    }

    // Apply combined transforms to weapon root
    this.weaponRoot.position.set(
      this.currentPos.x + shakeJitterX,
      this.currentPos.y + reloadOffsetY + shakeJitterY,
      this.currentPos.z + this.recoilOffsetZ
    );
    this.weaponRoot.rotation.set(
      -this.recoilPitch + reloadRotX,
      this.recoilYaw,
      this.barrelRoll || 0
    );

    // 6. Update Ejected Casings
    this.updateCasings(delta);
  }

  updateCasings(delta) {
    for (let i = this.casings.length - 1; i >= 0; i--) {
      const c = this.casings[i];
      c.life += delta;
      if (c.life >= c.maxLife || c.mesh.position.y <= 0.02) {
        c.mesh.visible = false;
        this.casings.splice(i, 1);
      } else {
        c.mesh.position.addScaledVector(c.velocity, delta);
        c.velocity.y -= 9.8 * delta; // Gravity
        c.mesh.rotation.x += c.rotVel.x * delta;
        c.mesh.rotation.y += c.rotVel.y * delta;
      }
    }
  }

  reset() {
    this.ammo = this.maxAmmo;
    this.isReloading = false;
    this.reloadProgress = 0;
    this.isAds = false;
    this.adsProgress = 0;
    this.recoilOffsetZ = 0;
    this.recoilPitch = 0;
    this.recoilYaw = 0;
    this.recoilBloom = 0;
    this.slideKick = 0;
  }
}
